/* eslint-disable typescript-extended-rules/file-naming-convention */
import { Component, Inject } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import {
	BeneficiaryReconciliationApplyTargetValue,
	BeneficiaryReconciliationItem,
} from '../../../../../modules/beneficiary-reconciliations/models/beneficiary-reconciliation.model';
import { TypeKinship } from '../../../../../shared/enums/type-kinship.enum';

export type BeneficiaryReconciliationTitularityResolutionResult =
	| {
			action: 'IGNORE_ITEM';
			applyOnly?: boolean;
			targetValue: BeneficiaryReconciliationApplyTargetValue;
	  }
	| {
			action: 'UPDATE_TITULARITY';
			applyOnly?: boolean;
			targetValue: BeneficiaryReconciliationApplyTargetValue;
	  }
	| {
			removePending: true;
	  };

interface TitularityResolutionDialogData {
	focusHolderCpf?: boolean;
	item: BeneficiaryReconciliationItem;
	targetValue: BeneficiaryReconciliationApplyTargetValue | null;
}

const NOT_INFORMED_LABEL = 'Não informado';

@Component({
	selector: 'app-beneficiary-reconciliation-titularity-resolution-modal',
	styleUrls: [
		'./beneficiary-reconciliation-titularity-resolution-modal.component.scss',
	],
	templateUrl:
		'./beneficiary-reconciliation-titularity-resolution-modal.component.html',
})
export class BeneficiaryReconciliationTitularityResolutionModalComponent {
	readonly currentSystemValue: Record<string, unknown>;
	readonly externalSnapshot: Record<string, unknown>;
	readonly isReadyToApply: boolean;
	readonly itemCarrier: string;
	readonly itemOrganization: string;
	readonly planHolderCpf: string;
	readonly planTypeLabel: string;
	readonly reviewReasonText: string;
	readonly reviewStatusLabel: string;
	readonly subjectBeneficiaryCpf: string;
	readonly subjectBeneficiaryName: string;
	readonly suggestedTargetValue: BeneficiaryReconciliationApplyTargetValue | null;
	readonly systemHolderCpf: string;
	readonly systemTypeLabel: string;
	readonly transitionDescription: string;
	readonly transitionTitle: string;
	readonly validHolderCpfControl = new FormControl('', [
		Validators.required,
		Validators.pattern(/^\D*(\d\D*){11}$/),
	]);

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: TitularityResolutionDialogData,
		private readonly dialogRef: MatDialogRef<BeneficiaryReconciliationTitularityResolutionModalComponent>,
	) {
		this.externalSnapshot = this.resolveExternalSnapshot();
		this.currentSystemValue = this.resolveCurrentSystemValue();
		this.planTypeLabel = this.toTitularityLabel(
			this.getDisplayValue(this.externalSnapshot, 'titularity', 'parentesco'),
		);
		this.subjectBeneficiaryName = this.resolveSubjectBeneficiaryName();
		this.subjectBeneficiaryCpf = this.resolveSubjectBeneficiaryCpf();
		this.systemTypeLabel = this.toTitularityLabel(
			this.getDisplayValue(this.currentSystemValue, 'titularity', 'parentesco'),
		);
		this.planHolderCpf = this.getFormattedCpf(
			this.getDisplayValue(this.externalSnapshot, 'holderCpf'),
		);
		this.systemHolderCpf = this.getFormattedCpf(
			this.getDisplayValue(this.currentSystemValue, 'holderCpf'),
		);
		this.isReadyToApply = this.resolveIsReadyToApply();
		this.suggestedTargetValue = this.resolveSuggestedTargetValue();
		this.reviewReasonText = this.resolveReviewReasonText();
		this.reviewStatusLabel = this.resolveReviewStatusLabel();
		this.transitionTitle = this.resolveTransitionTitle();
		this.transitionDescription = this.resolveTransitionDescription();
		this.itemCarrier = this.data.item.carrier || '-';
		this.itemOrganization =
			this.data.item.organizationName ||
			this.data.item.organizationCnpj ||
			this.data.item.organizationId ||
			'-';
		const pendingHolderCpf = this.getStringValue(
			this.data.targetValue?.holderCpf,
		);

		if (pendingHolderCpf) {
			this.validHolderCpfControl.setValue(pendingHolderCpf.replace(/\D/g, ''));
		}
	}

	get canResolveWithHolderCpf(): boolean {
		return !this.isReadyToApply && this.planTypeLabel === 'Dependente';
	}

	get hasPendingChange(): boolean {
		return Boolean(this.data.targetValue);
	}

	get planHolderCpfLabel(): string {
		if (this.planHolderCpf === '-') {
			return NOT_INFORMED_LABEL;
		}

		return this.canResolveWithHolderCpf
			? `${this.planHolderCpf} (esse CPF não é válido)`
			: this.planHolderCpf;
	}

	get shouldShowPlanHolderDetails(): boolean {
		return this.planTypeLabel === 'Dependente';
	}

	get shouldShowResolutionSummary(): boolean {
		return this.isReadyToApply || this.canResolveWithHolderCpf;
	}

	get shouldShowSystemHolderDetails(): boolean {
		return this.systemTypeLabel === 'Dependente';
	}

	get systemHolderCpfLabel(): string {
		return this.systemHolderCpf !== '-'
			? this.systemHolderCpf
			: NOT_INFORMED_LABEL;
	}

	onApplyOnly(): void {
		if (!this.suggestedTargetValue) {
			return;
		}

		this.dialogRef.close({
			action: 'UPDATE_TITULARITY',
			applyOnly: true,
			targetValue: this.suggestedTargetValue,
		});
	}

	onClose(): void {
		this.dialogRef.close();
	}

	onConfirmSuggested(): void {
		if (!this.suggestedTargetValue) {
			return;
		}

		this.dialogRef.close({
			action: 'UPDATE_TITULARITY',
			targetValue: this.suggestedTargetValue,
		});
	}

	onIgnore(): void {
		this.dialogRef.close({
			action: 'IGNORE_ITEM',
			targetValue: {},
		});
	}

	onRemove(): void {
		this.dialogRef.close({ removePending: true });
	}

	onSaveHolderCpf(): void {
		if (!this.canResolveWithHolderCpf) {
			return;
		}

		this.validHolderCpfControl.markAsTouched();

		if (this.validHolderCpfControl.invalid) {
			return;
		}

		this.dialogRef.close({
			action: 'UPDATE_TITULARITY',
			targetValue: {
				holderCpf: (this.validHolderCpfControl.value ?? '').replace(/\D/g, ''),
				typeKinship: this.resolveDefaultDependentTypeKinship(),
			},
		});
	}

	private getDisplayValue(
		source: Record<string, unknown>,
		...keys: string[]
	): string {
		for (const key of keys) {
			const value = this.getStringValue(source[key]);

			if (value) {
				return value;
			}
		}

		return '-';
	}

	private getFormattedCpf(value: string): string {
		const digits = value.replace(/\D/g, '');

		if (digits.length !== 11) {
			return value || '-';
		}

		return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
	}

	private getStringValue(value: unknown): string {
		return typeof value === 'string' ? value.trim() : '';
	}

	private resolveCurrentSystemValue(): Record<string, unknown> {
		const context = (this.data.item.context ?? {}) as Record<string, unknown>;
		const currentValue = context.currentValue;

		if (currentValue && typeof currentValue === 'object') {
			return currentValue as Record<string, unknown>;
		}

		return (this.data.item.dbValue ?? {}) as Record<string, unknown>;
	}

	private resolveDefaultDependentTypeKinship(): TypeKinship {
		const context = (this.data.item.context ?? {}) as Record<string, unknown>;
		const value = this.getStringValue(context.defaultDependentTypeKinship);

		return Object.values(TypeKinship).includes(value as TypeKinship)
			? (value as TypeKinship)
			: TypeKinship.FILHO;
	}

	private resolveExternalSnapshot(): Record<string, unknown> {
		const context = (this.data.item.context ?? {}) as Record<string, unknown>;
		const externalValue = context.externalValue;

		if (externalValue && typeof externalValue === 'object') {
			return externalValue as Record<string, unknown>;
		}

		return (this.data.item.externalValue ?? {}) as Record<string, unknown>;
	}

	private resolveIsReadyToApply(): boolean {
		const context = (this.data.item.context ?? {}) as Record<string, unknown>;

		return context.manualReviewRequired === false;
	}

	private resolveReadyActionText(): string {
		if (this.planTypeLabel === 'Titular') {
			return 'Salvar na fila para transformar este beneficiário em titular.';
		}

		if (
			this.systemTypeLabel === 'Dependente' &&
			this.planTypeLabel === 'Dependente'
		) {
			return 'Salvar na fila para trocar o CPF do titular deste beneficiário.';
		}

		if (this.planTypeLabel === 'Dependente') {
			return 'Salvar na fila para vincular este beneficiário ao CPF do titular.';
		}

		return 'Salvar na fila para atualizar a titularidade deste beneficiário.';
	}

	private resolveReviewReasonText(): string {
		const context = (this.data.item.context ?? {}) as Record<string, unknown>;
		const reason =
			this.getStringValue(context.titularityReviewReason) ||
			this.getStringValue(context.reviewReason);
		const messages: Record<string, string> = {
			HOLDER_AMBIGUOUS: 'Mais de um possível titular foi encontrado.',
			HOLDER_INACTIVE: 'Novo titular está inativo.',
			HOLDER_INVALID: 'CPF do novo titular inválido.',
			HOLDER_MISSING:
				'Informe o CPF do titular para adicionar esta alteração à fila.',
			HOLDER_NOT_FOUND: 'Novo titular não encontrado.',
			HOLDER_NOT_PRIMARY: 'Novo titular não está cadastrado como titular.',
			HOLDER_SCOPE_CONFLICT:
				'Novo titular está fora do escopo ou da organização permitida.',
			INSUFFICIENT_DATA:
				'Dados insuficientes para sugerir uma ação automática.',
		};

		if (!this.isReadyToApply) {
			return (
				messages[reason] ||
				'Revise os dados antes de adicionar esta alteração à fila.'
			);
		}

		return this.resolveReadyActionText();
	}

	private resolveReviewStatusLabel(): string {
		if (this.isReadyToApply) {
			return 'Pronto para aplicar';
		}

		if (this.canResolveWithHolderCpf) {
			return 'Precisa informar titular';
		}

		return 'Revisão obrigatória';
	}

	private resolveSubjectBeneficiaryCpf(): string {
		const context = (this.data.item.context ?? {}) as Record<string, unknown>;
		const subject =
			context.subjectBeneficiary &&
			typeof context.subjectBeneficiary === 'object'
				? (context.subjectBeneficiary as Record<string, unknown>)
				: {};
		const cpf =
			this.getStringValue(subject.cpf) || this.data.item.beneficiaryCpf || '';

		return this.getFormattedCpf(cpf);
	}

	private resolveSubjectBeneficiaryName(): string {
		const context = (this.data.item.context ?? {}) as Record<string, unknown>;
		const subject =
			context.subjectBeneficiary &&
			typeof context.subjectBeneficiary === 'object'
				? (context.subjectBeneficiary as Record<string, unknown>)
				: {};

		return (
			this.getStringValue(subject.name) || this.data.item.beneficiaryName || '-'
		);
	}

	private resolveSuggestedTargetFallback(): BeneficiaryReconciliationApplyTargetValue | null {
		if (!this.isReadyToApply) {
			return null;
		}

		if (this.planTypeLabel === 'Titular') {
			return { typeKinship: TypeKinship.TITULAR };
		}

		if (this.planTypeLabel !== 'Dependente') {
			return null;
		}

		const holderCpf = this.planHolderCpf.replace(/\D/g, '');

		return holderCpf
			? {
					holderCpf,
					typeKinship: this.resolveDefaultDependentTypeKinship(),
				}
			: null;
	}

	private resolveSuggestedTargetValue(): BeneficiaryReconciliationApplyTargetValue | null {
		if (this.data.targetValue) {
			return this.data.targetValue;
		}

		const context = (this.data.item.context ?? {}) as Record<string, unknown>;
		const suggestedTargetValue = context.suggestedTargetValue;

		if (suggestedTargetValue && typeof suggestedTargetValue === 'object') {
			return suggestedTargetValue as BeneficiaryReconciliationApplyTargetValue;
		}

		return this.resolveSuggestedTargetFallback();
	}

	private resolveTransitionDescription(): string {
		if (this.planTypeLabel === 'Titular') {
			return 'Ao aplicar, o vínculo com o titular atual será removido.';
		}

		if (
			this.systemTypeLabel === 'Dependente' &&
			this.planTypeLabel === 'Dependente'
		) {
			return 'Ao aplicar, somente este beneficiário passará para o novo titular.';
		}

		if (this.planTypeLabel === 'Dependente') {
			return 'Ao aplicar, somente este beneficiário será vinculado ao titular indicado.';
		}

		return 'Ao aplicar, somente este beneficiário será alterado.';
	}

	private resolveTransitionTitle(): string {
		if (
			this.systemTypeLabel === 'Dependente' &&
			this.planTypeLabel === 'Dependente'
		) {
			return 'Dependente trocará de titular';
		}

		if (this.systemTypeLabel === 'Dependente') {
			return 'Dependente passará a ser titular';
		}

		if (this.planTypeLabel === 'Dependente') {
			return 'Titular passará a ser dependente';
		}

		return 'Atualizar titularidade';
	}

	private toTitularityLabel(value: string): string {
		const normalizedValue = value.trim().toUpperCase();

		if (normalizedValue === 'T' || normalizedValue === 'TITULAR') {
			return 'Titular';
		}

		if (!normalizedValue || normalizedValue === '-') {
			return '-';
		}

		return 'Dependente';
	}
}
